// Gemeinsame Zahlungsart-Ableitung für App-Zahlungen (PayPal).
// docs/paypal_app_zahlungen_konzept.md 7.5/8.5 — EINE gemeinsame Ableitung für
// alle Listen, Tabs und Exporte. Maßgeblich ist ausschließlich der
// paymentStatus, niemals nur paymentMethod: eine gewählte, aber fehlgeschlagene
// App-Zahlung zählt als Fahrzeug-Umsatz (der Fahrer hat bar kassiert).

export const APP_PAID_PAYMENT_STATUSES = new Set(['captured', 'partially_refunded', 'refunded']);
export const APP_PENDING_PAYMENT_STATUSES = new Set([
  'approval_pending', 'authorized', 'reauthorization_required', 'capture_pending'
]);

export const getPaymentCategory = (ride) => {
  if (ride.paymentMethod === 'paypal') {
    const status = String(ride.paymentStatus || '');
    if (APP_PAID_PAYMENT_STATUSES.has(status)) return 'app_paid';
    if (APP_PENDING_PAYMENT_STATUSES.has(status)) return 'app_pending';
    if (status === 'capture_failed') return 'app_failed';
    if (ride.paymentFallback === 'cash_collected') return 'app_to_cash';
  }
  return 'in_vehicle';
};

export const PAYMENT_CATEGORY_LABELS = {
  app_paid: 'Per App bezahlt',
  app_pending: 'App-Zahlung offen',
  app_failed: 'App-Zahlung fehlgeschlagen',
  app_to_cash: 'App → Bar kassiert',
  in_vehicle: 'Im Fahrzeug'
};

export const getPaymentCategoryLabel = (category) => PAYMENT_CATEGORY_LABELS[category] || category;

/** Per App bezahlte Fahrten zählen mit dem Capture-Betrag, nicht mit fare. */
export const isAppPaidRide = (ride) => getPaymentCategory(ride) === 'app_paid';

// Chip-Deskriptor ({ label, color, variant, paypalIcon }) anhand der Kategorie.
// `approvalPending` unterdrückt den PayPal-Chip im Zustand approval_pending,
// der sich mit dem Fahrtstatus „Zahlung ausstehend" deckt (kein redundanter Chip).
const chipForCategory = (category, { showInVehicle = false, approvalPending = false } = {}) => {
  switch (category) {
    case 'app_paid':
      // PayPal-Blau (info) wie der vorautorisierte „PayPal"-Chip, aber gefüllt
      // als finaler, bezahlter Zustand.
      return { label: 'Per App bezahlt', color: 'info', variant: 'filled', paypalIcon: true };
    case 'app_pending':
      if (approvalPending) return null;
      return { label: 'PayPal', color: 'info', variant: 'outlined', paypalIcon: true };
    case 'app_failed':
      return { label: 'App-Zahlung fehlgeschlagen', color: 'error', variant: 'outlined' };
    case 'app_to_cash':
      return { label: 'App → Bar kassiert', color: 'warning', variant: 'outlined' };
    default:
      return showInVehicle ? { label: 'Im Fahrzeug', color: 'default', variant: 'outlined' } : null;
  }
};

/**
 * MUI-Chip-Deskriptor für die Zahlungsart einer Fahrt — oder null, wenn kein
 * Chip gezeigt werden soll. Reine Fahrzeug-Fahrten geben standardmäßig null
 * zurück (Listen nicht überladen); mit { showInVehicle: true } auch dieser Chip.
 */
export const getPaymentChip = (ride, { showInVehicle = false } = {}) =>
  chipForCategory(getPaymentCategory(ride), {
    showInVehicle,
    approvalPending: String(ride.paymentStatus || '') === 'approval_pending',
  });

/** Wie getPaymentChip, aber für eine bereits abgeleitete Kategorie — z. B. in
 *  Berichts-Zeilen, die `paymentCategory` schon vorberechnet halten. */
export const getPaymentChipForCategory = (category, options = {}) =>
  chipForCategory(category, options);
